/**
 * 手写签名控件（`signature` 字段 · C-23）
 * -----------------------------------------------------------------------------
 * 交互（2026-10-18 改版）：
 *   卡片里**只有一个入口**（虚线框 / 已签名缩略图）→ 点开**独立的签名浮层** →
 *   浮层里横屏手写 → 「完成签名」上传（`purpose=FORM_SIGNATURE`）→ 浮层关闭。
 *
 * 为什么不再把画布直接摆在卡片里：
 *   ① 卡片宽度只有 320 出头，签名区被挤成一条窄带，手指一写就出框；
 *   ② 页面本身可滚动，指着签名时页面跟着滑，一笔就断；
 *   ③ 独立浮层可以给足高度、锁住底层滚动，写起来才像「签字」。
 *
 * 口径不变：
 *   - **空白画布不允许提交**（`hasInk` 守卫）—— 空 PNG 会被校验器判「签名结果无效」；
 *   - 签名只能**整幅重签**（不存在「半个签名」），重签覆盖 assetId；
 *   - 重开草稿时 `value` 只是 assetId（画不出东西），画布内容靠 `previewUrl`
 *     **把存档图铺回画布**来还原（2026-10-27 起，之前是空白画布）；
 *   - 指针事件统一走 Pointer Events（触屏 / 鼠标同一套）；画布按 devicePixelRatio
 *     放大绘制，避免高分屏线条发虚。
 *
 * 🔴 **打开浮层要把原签名铺回画布**（2026-10-27 用户拍板，勿改回「一律空白」）
 *   之前每次打开都是一块空白画布 + 一句「请在此处签名」，缩略图又正好被浮层盖住 ——
 *   用户点「查看」的本意可能是核对「签的是不是我的名字」，结果看到的是白板，
 *   等于签完就再也看不到自己签的东西。现在的口径：
 *     · 有存档签名 → 先把图铺进画布，**它就是当前值**；想重签自己点「清除」；
 *     · 铺了底图但没动笔就点「完成签名」→ 直接关浮层，**不重传**（不白白多一份对象）；
 *     · 点「清除」→ 画布清空、按钮禁用，此时才必须重画才能提交（与原来一致）；
 *     · 底图加载失败（资产被留存清理 / 链接过期）→ 当作没有底图，落回空白画布，
 *       不报错：这是「看不到旧图」的降级，不是用户这次操作的失败。
 *   画布仍**不铺任何底色**：`drawImage` 只叠墨迹，导出依旧是透明 PNG（见下条）。
 *
 * 🔴 **存档必须是「透明 PNG」（2026-10-22 用户拍板，勿改）**
 *   画布只用 `clearRect` 清空、**从不 `fillRect` 铺底色**，导出固定
 *   `toBlob(..., 'image/png')`（本地回显的 `toDataURL` 同理）。落盘实测：colorType 6
 *   (RGBA)、四角 `alpha=0`、不透明像素仅墨迹 `#0f172a`、**近白像素 0 个**。
 *
 *   所以：
 *     · **禁止**给画布加白底 / 米色底 / 纸纹（哪怕「打印更像真签名」）；
 *     · **禁止**改成 `image/jpeg`（JPEG 无 alpha 通道，透明区会被烤成黑色或白色）；
 *     · 需要白底的消费方（报名表 PDF、合同附件）**在生成侧自己合成**，不要回来改存档。
 *   签名是**原件级凭证**：底一旦被烤进去就不可逆，而透明原件永远能派生出白底版本，
 *   反过来做不到。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { uploadFormAsset } from '../../api/h5';
import { FormEditIcon } from '../icons';
import { Button } from '../layout';

export interface SignatureCanvasProps {
  /** 当前值 = assetId 字符串（undefined = 未签名） */
  value: unknown;
  /**
   * 已存档签名的访问链接（来自 `RegistrationDetail.signatureUrls`）。
   *
   * 为什么需要它：`value` 只是 `assetId`，拿它画不出任何东西。没有这张图时，
   * 学员切一下步骤回来（组件卸载重挂）签名就只剩一句「已签名」——
   * 而那正是他要核对的东西。**它不是本地状态**，所以刷新、换设备都还在。
   */
  previewUrl?: string | null;
  disabled: boolean;
  /** 归属的报名单：带上它，提交时服务端才能删掉被重新签署替换掉的旧签名 */
  registrationId?: string;
  onChange: (next: string | undefined) => void;
  /** 失焦：把刚落库的 assetId 带上去，父级据此校验新值 */
  onBlur: (next?: unknown) => void;
}

/** 浮层里给足书写高度：约 4 成屏，最低 220px */
function boardHeight(): number {
  if (typeof window === 'undefined') return 260;
  return Math.max(220, Math.min(300, Math.round(window.innerHeight * 0.38)));
}

export function SignatureCanvas({
  value,
  previewUrl = null,
  disabled,
  registrationId,
  onChange,
  onBlur,
}: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  /**
   * 用户**本次**是否真的落过笔（铺上去的底图不算）。
   * 只用来回答一句：「他只是看了一眼，还是要换一张」——
   * 没动笔就点「完成签名」不该重传，否则每看一次就多一个存储对象、assetId 也白换。
   */
  const dirtyRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 上传失败就地提示（不弹 Dialog：那会把用户写好的那一笔挡住） */
  const [failText, setFailText] = useState<string | null>(null);
  /** 本次会话内画完的签名图（dataURL），重开页面拿不到，只作视觉回显 */
  const [preview, setPreview] = useState<string | null>(null);

  const hasSigned = typeof value === 'string' && value.trim() !== '';

  /**
   * 实际要显示的签名图：**刚写完的本地图优先**，其次才是服务端下发的那张。
   *
   * 顺序不能反：`previewUrl` 是进页面那一刻取的快照，重签之后服务端虽然有新图，
   * 但这份 URL 要等详情重新拉取才更新 —— 先看 `preview` 才能做到「签完立刻是新的那一张」。
   */
  const shownPreview = preview ?? previewUrl;

  /** 画布尺寸随容器定宽 + DPR 放大；重签（清空）时也调用 */
  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 320;
    const cssHeight = canvas.clientHeight || 260;
    canvas.width = Math.floor(cssWidth * ratio);
    canvas.height = Math.floor(cssHeight * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.clearRect(0, 0, cssWidth, cssHeight);
    hasInkRef.current = false;
    dirtyRef.current = false;
    setHasInk(false);
  }, []);

  // 浮层打开后才挂载 canvas，挂载完立刻按容器尺寸初始化
  useEffect(() => {
    if (open) resetCanvas();
  }, [open, resetCanvas]);

  /**
   * 把已存档的签名铺回画布（2026-10-27 用户要求：点「查看 / 重签」要能看见原来签的）。
   *
   * 三个必须小心的地方：
   *   ① **等尺寸定下来再画** —— 本 effect 排在 `resetCanvas` 之后，此时
   *      `canvas.width/height` 已按 DPR 设好、`ctx` 也已 `scale(ratio)`，
   *      所以这里全部用 CSS 像素坐标。
   *   ② **底图异步回来时用户可能已经动笔** —— 那就放弃铺图，否则会把他刚写的那笔盖掉。
   *   ③ 图挂了**不报错**（资产被留存清理 / 链接过期）：退回空白画布，让他重画。
   */
  useEffect(() => {
    if (!open) return;
    const source = shownPreview;
    if (!source) return;

    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      if (hasInkRef.current) return;

      // 等比缩放居中（contain）：签名框与存档图宽高比通常接近，拉伸会让人以为笔画变了
      const boxWidth = canvas.clientWidth || 320;
      const boxHeight = canvas.clientHeight || 240;
      const natural = { w: image.naturalWidth, h: image.naturalHeight };
      if (!natural.w || !natural.h) return;
      const scale = Math.min(boxWidth / natural.w, boxHeight / natural.h);
      const width = natural.w * scale;
      const height = natural.h * scale;
      ctx.drawImage(image, (boxWidth - width) / 2, (boxHeight - height) / 2, width, height);

      hasInkRef.current = true;
      setHasInk(true);
    };
    image.src = source;

    return () => {
      cancelled = true;
    };
  }, [open, shownPreview]);

  // 浮层打开期间锁住底层滚动，否则一笔画到一半页面会跟着滑
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const pointOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const startDraw = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (busy) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointOf(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // 点一下也要留痕（否则「点了个点」被判空白）
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    drawingRef.current = true;
    dirtyRef.current = true;
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  };

  const moveDraw = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointOf(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    if (busy) return;
    setFailText(null);
    resetCanvas();
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
  };

  const confirm = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) return;
    // 只是打开看了一眼、没动笔：原样关掉即可。重传会白换一个 assetId、
    // 在白名单存储里多一份对象，而且值其实一点没变。
    if (!dirtyRef.current) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setFailText(null);
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((result) => resolve(result), 'image/png'),
      );
      if (!blob) throw new Error('签名图生成失败，请重试');
      const result = await uploadFormAsset(blob, 'signature.png', 'FORM_SIGNATURE', registrationId);
      setPreview(dataUrl);
      onChange(result.assetId);
      onBlur(result.assetId);
      setOpen(false);
    } catch (error) {
      // 失败留在浮层里，用户写的那一笔不丢
      setFailText(error instanceof ApiError ? error.message : '签名上传失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sign-field">
      <button
        type="button"
        className={`sign-trigger${hasSigned ? ' sign-trigger--done' : ''}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={hasSigned ? '已签名，点击重新签名' : '点击此处签名'}
      >
        {shownPreview ? (
          <img className="sign-trigger__img" src={shownPreview} alt="手写签名" />
        ) : (
          <>
            <span className="sign-trigger__icon">
              <FormEditIcon size={20} />
            </span>
            <span className="sign-trigger__title">{hasSigned ? '已签名' : '点击此处签名'}</span>
            <span className="sign-trigger__hint">
              {hasSigned ? '点击查看原签名，需要可点「清除」重签' : '用手指或触控笔在书写区手写'}
            </span>
          </>
        )}
        {hasSigned && shownPreview ? (
          <span className="sign-trigger__tag">已签名 · 重签</span>
        ) : null}
      </button>

      {open ? (
        <div className="sign-panel" role="dialog" aria-modal="true" aria-label="手写签名">
          <div className="sign-panel__sheet">
            <div className="sign-panel__bar">
              <button type="button" className="sign-panel__act" onClick={close}>
                取消
              </button>
              <span className="sign-panel__title">手写签名</span>
              <span className="sign-panel__act" aria-hidden="true" />
            </div>

            <div className="sign-panel__stage">
              <canvas
                ref={canvasRef}
                className="sign-panel__board"
                style={{ height: boardHeight() }}
                onPointerDown={startDraw}
                onPointerMove={moveDraw}
                onPointerUp={endDraw}
                onPointerLeave={endDraw}
                onPointerCancel={endDraw}
              />
              {hasInk ? null : <span className="sign-panel__watermark">请在此处签名</span>}
            </div>

            <p className="sign-panel__note">
              {failText ? (
                <span className="sign-panel__fail">{failText}</span>
              ) : (
                <>
                  {hasSigned ? '已显示原签名；要重签请先点「清除」。' : ''}
                  签名作为报名承诺的电子凭证存档，请使用与本人身份证姓名一致的常用签名
                </>
              )}
            </p>

            <div className="sign-panel__foot">
              <Button variant="ghost" disabled={!hasInk || busy} onClick={clear}>
                清除
              </Button>
              <Button
                variant="primary"
                className="sign-panel__submit"
                disabled={!hasInk || busy}
                loading={busy}
                onClick={() => void confirm()}
              >
                完成签名
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
